import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  PERMISSIONS,
  PQRSD_PRIORITY_COLORS,
  PQRSD_PRIORITY_LABELS,
  PQRSD_STATUS,
  PQRSD_TYPES,
} from "../../constants";
import { getPqrsd, listInfoRequests } from "../../services/backend/PqrsdService";
import { ANSWER_TAGS } from "./config/documentTemplates";
import { STATUS_CHIP } from "./InfoRequestPanel";
import { STATUS_COLOR, STATUS_LABELS } from "./PqrsdAdminDetail";

import { pqrsdText } from "./pqrsdLabel";
// The PQRSD a flow answers, shown as a collapsible panel at the top of the
// task's sidebar (the flow column next to the editor). Everyone in the chain — whoever elaborates, reviews, approves or signs —
// checks the answer against the petition, so it has to be in front of them
// without leaving the task. Read-only; acting on the PQRSD happens in its own
// page.

const EXPANDED_KEY = "royalplus.pqrsdFlowContext.expanded";

// Browser storage can be missing or throw (private mode, blocked site data).
function readExpanded() {
  try {
    return window.localStorage.getItem(EXPANDED_KEY) !== "0";
  } catch (_) {
    return true;
  }
}
function writeExpanded(value) {
  try {
    window.localStorage.setItem(EXPANDED_KEY, value ? "1" : "0");
  } catch (_) {
    // Only a convenience.
  }
}

function formatDate(value, withTime = false) {
  if (!value) return "—";
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return "—";
  return new Intl.DateTimeFormat("es-CO", {
    timeZone: "America/Bogota",
    day: "2-digit",
    month: "short",
    year: "numeric",
    ...(withTime ? { hour: "2-digit", minute: "2-digit", hour12: false } : {}),
  }).format(d);
}

function dueChip(p) {
  if (p.slaPaused) return { label: "Término suspendido", color: "secondary" };
  if (!p.slaDeadline || [PQRSD_STATUS.CERRADO, PQRSD_STATUS.RESPONDIDO, PQRSD_STATUS.DESISTIDO, PQRSD_STATUS.ANULADO].includes(p.status)) {
    return null;
  }
  const days = Math.ceil((new Date(p.slaDeadline).getTime() - Date.now()) / 86400000);
  if (Number.isNaN(days)) return null;
  const color = days < 0 ? "error" : days <= 3 ? "warning" : "default";
  return { label: `Vence ${formatDate(p.slaDeadline)}`, color };
}

// The assignees as the PQRSD page lists them (legacy single-assignee docs too).
function assigneeNames(p) {
  const list = Array.isArray(p.assignees) && p.assignees.length
    ? p.assignees
    : p.assignee
      ? [{ userId: p.assignee, name: p.assigneeName, email: p.assigneeEmail }]
      : [];
  return list.map((a) => a.name || a.email || a.userId).filter(Boolean);
}

function Field({ label, children }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
      {typeof children === "string" || typeof children === "number"
        ? <Typography variant="body2">{children}</Typography>
        : children}
    </Box>
  );
}

function Section({ title, children }) {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>{title}</Typography>
      {children}
    </Box>
  );
}

function InfoRequests({ requests }) {
  if (!requests.length) {
    return <Typography variant="body2" color="text.secondary">Sin requerimientos de información.</Typography>;
  }
  return (
    <Stack spacing={1.5} divider={<Divider flexItem />}>
      {requests.slice().reverse().map((r) => {
        const chip = STATUS_CHIP[r.status] || { label: r.status, color: "default" };
        const items = [...(r.items || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
        return (
          <Box key={r.id}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
              <Chip size="small" label={chip.label} color={chip.color} />
              {r.sentAt && (
                <Typography variant="caption" color="text.secondary">Enviado {formatDate(r.sentAt)}</Typography>
              )}
              {r.repliedAt && (
                <Typography variant="caption" color="text.secondary">· Respondido {formatDate(r.repliedAt)}</Typography>
              )}
              {r.requerimientoFileUrl && (
                <Link href={r.requerimientoFileUrl} target="_blank" rel="noopener" variant="caption"
                  sx={{ display: "inline-flex", alignItems: "center", gap: 0.25, fontFamily: "monospace" }}>
                  <DescriptionIcon sx={{ fontSize: 14 }} /> {r.requerimientoFiledId || "Documento"}
                </Link>
              )}
            </Stack>
            {r.note && (
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", mb: 0.5 }}>“{r.note}”</Typography>
            )}
            <Stack spacing={1} sx={{ pl: 1, borderLeft: 3, borderColor: "divider" }}>
              {items.map((it) => (
                <Box key={it.id} sx={{ pl: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {it.kind === "file" ? it.label : it.header}
                  </Typography>
                  {it.kind === "file" ? (
                    it.file?.url ? (
                      <Link href={it.file.url} target="_blank" rel="noopener" variant="body2"
                        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
                        <DescriptionIcon fontSize="small" /> {it.file.name || "Archivo"}
                      </Link>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Sin cargar</Typography>
                    )
                  ) : (
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}
                      color={it.answer ? "text.primary" : "text.secondary"}>
                      {it.answer || "Sin responder"}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Stack>
  );
}

// The flow's merge tags as "<<tag>> → value", answer tags in the order the
// admin sees them. `recipient` is the DTO and `subject` mirrors `asunto`.
function tagRows(tags) {
  if (!tags || typeof tags !== "object") return [];
  const order = (k) => {
    const i = ANSWER_TAGS.indexOf(k);
    return i === -1 ? ANSWER_TAGS.length : i;
  };
  return Object.keys(tags)
    .filter((k) => k !== "recipient" && k !== "subject" && ["string", "number"].includes(typeof tags[k]))
    .sort((a, b) => order(a) - order(b) || a.localeCompare(b))
    .map((k) => [k, String(tags[k])]);
}

export default function PqrsdFlowContext({ radicadoId, tags }) {
  const user = useSelector((s) => s.user.value);
  const canOpenPqrsd = (user?.permissions || []).includes(PERMISSIONS.PQRSD.READ);

  const [expanded, setExpanded] = useState(readExpanded);
  const [loading, setLoading] = useState(true);
  const [pqrsdDoc, setPqrsdDoc] = useState(null);
  const [requests, setRequests] = useState([]);
  const [showTags, setShowTags] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const [doc, list] = await Promise.all([getPqrsd(radicadoId), listInfoRequests(radicadoId)]);
    setPqrsdDoc(doc);
    setRequests(Array.isArray(list) ? list : []);
    setLoading(false);
  }, [radicadoId]);

  useEffect(() => {
    load();
  }, [load]);

  const rows = useMemo(() => tagRows(tags), [tags]);

  const toggle = (_, value) => {
    setExpanded(value);
    writeExpanded(value);
  };

  const p = pqrsdDoc?.pqrsd || {};
  const anonymous = !!p.anonymous;
  const solicitor = anonymous
    ? "Ciudadano anónimo"
    : `${p.solicitor?.name || ""} ${p.solicitor?.surname || ""}`.trim() || "—";
  const subject = pqrsdDoc?.subject || p.subject || "—";
  const due = pqrsdDoc ? dueChip(p) : null;
  const annexes = Array.isArray(pqrsdDoc?.annexes) ? pqrsdDoc.annexes.filter((a) => a?.url || a?.name) : [];

  return (
    <Accordion
      expanded={expanded}
      onChange={toggle}
      disableGutters
      variant="outlined"
      sx={{ mb: 1, "&:before": { display: "none" } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ "& .MuiAccordionSummary-content": { minWidth: 0 } }}>
        <Box sx={{ minWidth: 0, width: "100%" }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Chip size="small" color="primary" label={pqrsdText("PQRSD")} />
            <Typography variant="body2" sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}>{radicadoId}</Typography>
            {loading && !pqrsdDoc && <CircularProgress size={14} />}
            {pqrsdDoc && p.status && (
              <Chip size="small" variant="outlined" color={STATUS_COLOR[p.status] || "default"}
                label={STATUS_LABELS[p.status] || p.status} />
            )}
            {due && <Chip size="small" color={due.color} label={due.label} />}
          </Stack>
          {pqrsdDoc ? (
            <>
              <Typography variant="body2" noWrap title={subject} sx={{ mt: 0.75, fontWeight: 500 }}>
                {subject}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {PQRSD_TYPES[p.type] || p.type || ""} · {solicitor}
              </Typography>
            </>
          ) : !loading && (
            <Typography variant="body2" color="error" sx={{ mt: 0.75 }}>{pqrsdText("No se encontró la PQRSD.")}</Typography>
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ pt: 0 }}>
        {pqrsdDoc && (
          <Box>
            {p.status === PQRSD_STATUS.ANULADO && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {pqrsdText("Esta PQRSD fue anulada: no admite respuestas.")}
              </Alert>
            )}
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mb: 1 }}>
              <Tooltip title={pqrsdText("Volver a cargar la PQRSD")}>
                <span>
                  <IconButton size="small" onClick={load} disabled={loading}>
                    {loading ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              {canOpenPqrsd && (
                // A new tab: leaving the task would drop the editor.
                <Button
                  size="small"
                  component={RouterLink}
                  to={`/pqrsd/admin/${encodeURIComponent(radicadoId)}`}
                  target="_blank"
                  rel="noopener"
                  endIcon={<OpenInNewIcon fontSize="small" />}
                >
                  {pqrsdText("Abrir PQRSD")}
                </Button>
              )}
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 1.5,
                mb: 2,
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
              }}
            >
              <Field label="Tipo">{PQRSD_TYPES[p.type] || p.type || "—"}</Field>
              <Field label="Canal">{p.channel || "web"}</Field>
              <Field label="Prioridad">
                {p.priority ? (
                  <Chip size="small" color={PQRSD_PRIORITY_COLORS[p.priority] || "default"}
                    label={PQRSD_PRIORITY_LABELS[p.priority] || p.priority} />
                ) : <Typography variant="body2">—</Typography>}
              </Field>
              <Field label="Dependencia">{p.groupName || "—"}</Field>
              <Field label="Recibida">{formatDate(pqrsdDoc.createdAt, true)}</Field>
              <Field label="Fecha límite">
                <Typography variant="body2">
                  {formatDate(p.slaDeadline, true)}
                  {p.slaPaused ? " (suspendido)" : ""}
                </Typography>
              </Field>
              <Field label="Asignado a">{assigneeNames(p).join(", ") || "—"}</Field>
              <Field label="Estado">{STATUS_LABELS[p.status] || p.status || "—"}</Field>
            </Box>

            <Section title="Solicitante">
              <Typography variant="body2">{solicitor}</Typography>
              {!anonymous && (
                <>
                  {(p.solicitor?.idType || p.solicitor?.idNumber) && (
                    <Typography variant="body2" color="text.secondary">
                      {p.solicitor?.idType} {p.solicitor?.idNumber}
                    </Typography>
                  )}
                  {p.email && <Typography variant="body2" color="text.secondary">{p.email}</Typography>}
                </>
              )}
            </Section>
            <Section title="Asunto">
              <Typography variant="body2">{subject}</Typography>
            </Section>
            <Section title="Descripción">
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>{p.body || "—"}</Typography>
            </Section>
            <Section title="Anexos">
              {annexes.length ? (
                <Stack spacing={0.5} alignItems="flex-start">
                  {annexes.map((a, i) => (
                    <Button
                      key={`${a.name}-${i}`}
                      size="small"
                      variant="outlined"
                      startIcon={<DownloadIcon />}
                      href={a.url || undefined}
                      disabled={!a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ textTransform: "none", justifyContent: "flex-start" }}
                    >
                      {a.name || `Anexo ${i + 1}`}
                    </Button>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">Sin anexos.</Typography>
              )}
            </Section>
            {pqrsdDoc.fileUrl && (
              <Button size="small" startIcon={<DescriptionIcon />} href={pqrsdDoc.fileUrl} target="_blank" rel="noopener noreferrer" sx={{ mb: 2 }}>
                {pqrsdText("PDF radicado de la PQRSD")}
              </Button>
            )}
            <Section title="Requerimientos de información">
              <InfoRequests requests={requests} />
            </Section>
            {rows.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Button size="small" onClick={() => setShowTags((v) => !v)}
                  endIcon={<ExpandMoreIcon sx={{ transform: showTags ? "rotate(180deg)" : "none" }} />}>
                  Datos que se insertan al radicar
                </Button>
                {showTags && (
                  <Box
                    component="dl"
                    sx={{ display: "grid", gridTemplateColumns: "max-content 1fr", columnGap: 2, rowGap: 0.5, m: 0, mt: 1 }}
                  >
                    {rows.map(([k, v]) => (
                      <React.Fragment key={k}>
                        <Typography component="dt" variant="caption" sx={{ fontFamily: "monospace" }}>{`<<${k}>>`}</Typography>
                        <Typography component="dd" variant="caption" sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{v}</Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
